import { catchError, concat, defer, EMPTY, filter, first, ignoreElements, map, Observable, of, share, Subject, Subscription, switchMap, tap } from 'rxjs';

import { AgentWsConnection } from '../ws/agent-ws.connection';
import {
    AGENT_ERROR_CODE,
    AGENT_FRONTEND_VERSION,
    AGENT_PROTOCOL_VERSION,
    ClientCommand,
    ServerEvent,
    WsBlockDeclaration,
    WsReadyEvent,
    WsErrorEvent,
    WsSessionsEvent,
    WsSessionTitleEvent,
} from '../ws/agent-ws.types';
import { ChatAgentSessionProvider } from './chat-agent-session.provider';
import { AgentTool } from '../tools/agent-tool.types';

type ChatAgentProviderMode = 'idle' | 'loading_sessions' | 'creating_session' | 'opening_session' | 'closing_session' | 'active_session';

const INITIAL_RECONNECT_DELAY_MS = 500;
const MAX_RECONNECT_DELAY_MS = 30_000;

/** Провайдер общего подключения к агенту и операций верхнего уровня Chat V2. */
export class ChatAgentProvider {
    private readonly wsUrl: string;
    private readonly tabId: string;
    private readonly blocks: WsBlockDeclaration[];
    private readonly tools: AgentTool[];
    private readonly eventsSubject = new Subject<ServerEvent>();
    private readonly sessionTakenOverSubject = new Subject<void>();
    /** Реактивные обновления заголовков сессий (`session_title`). */
    public readonly sessionTitle$ = this.eventsSubject.pipe(
        filter((event): event is WsSessionTitleEvent => event.kind === 'session_title'),
    );
    /** Диалог забрало более новое соединение: локальную сессию нужно снять, но не переподключаться. */
    public readonly sessionTakenOver$ = this.sessionTakenOverSubject.asObservable();
    private ws: AgentWsConnection | null = null;
    private wsSubscription: Subscription | null = null;
    private wsInitialized = false;
    private mode: ChatAgentProviderMode = 'idle';
    private currentSessionProvider: ChatAgentSessionProvider | null = null;
    private pendingOperation$: Observable<unknown> = EMPTY;
    private destroyed = false;
    private reconnectAttempt = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private reconnectSubscription: Subscription | null = null;
    private isReconnecting = false;

    public constructor(wsUrl: string, tabId: string, blocks: WsBlockDeclaration[] = [], tools: AgentTool[] = []) {
        this.wsUrl = wsUrl;
        this.tabId = tabId;
        this.blocks = blocks;
        this.tools = tools;
    }

    /**
     * Получить страницу списка сессий.
     * Без `before` — первая (самые свежие). С `before` — более старые, чем этот sessionId.
     */
    public getSessions(before?: string): Observable<WsSessionsEvent> {
        return this.chainOperation(this.requestReadyConnection$('loading_sessions').pipe(
            switchMap(() => {
                this.send(before ? { type: 'get_sessions', before } : { type: 'get_sessions' });
                return this.eventsSubject.pipe(
                    first((event): event is WsSessionsEvent | WsErrorEvent => {
                        if (event.kind === 'error') {
                            return true;
                        }

                        if (event.kind !== 'sessions') {
                            return false;
                        }

                        if (!before) {
                            return !event.before;
                        }

                        return !event.before || event.before === before;
                    }),
                );
            }),
            map(event => {
                this.mode = this.currentSessionProvider ? 'active_session' : 'idle';
                if (event.kind === 'sessions') return event;
                throw event;
            }),
        ));
    }

    /** Создать новую сессию и вернуть её session provider. */
    public createSessionProvider(): Observable<ChatAgentSessionProvider> {
        return this.chainOperation(this.requestReadyConnection$('creating_session').pipe(
            switchMap(() => this.leaveSessionIfNeeded$()),
            switchMap(() => {
                this.mode = 'creating_session';
                this.send({ type: 'create_session' });
                return this.eventsSubject.pipe(first(e => e.kind === 'session_ready' || e.kind === 'error'));
            }),
            map(event => {
                if (event.kind === 'session_ready') return this.activateSessionProvider(event.sessionId);
                this.mode = 'idle';
                throw event;
            }),
        ));
    }

    /** Получить provider существующей сессии, переиспользуя текущую сессию при совпадении ID. */
    public getSessionProvider(sessionId: string): Observable<ChatAgentSessionProvider> {
        if (this.currentSessionProvider?.id === sessionId) {
            return of(this.currentSessionProvider);
        }

        return this.chainOperation(this.requestReadyConnection$('opening_session').pipe(
            switchMap(() => this.leaveSessionIfNeeded$()),
            switchMap(() => {
                this.mode = 'opening_session';
                this.send({ type: 'join_session', sessionId });
                return this.eventsSubject.pipe(first(e => e.kind === 'session_ready' || e.kind === 'error'));
            }),
            map(event => {
                if (event.kind === 'session_ready') return this.activateSessionProvider(event.sessionId);
                this.mode = 'idle';
                throw event;
            }),
        ));
    }

    /** Сбросить выбранную сессию, сохранив общее соединение открытым. */
    public closeSessionProvider(): Observable<void> {
        if (!this.ws || !this.currentSessionProvider) {
            this.destroyCurrentSessionProvider();
            return of(undefined);
        }

        return this.chainOperation(this.requestReadyConnection$('closing_session').pipe(
            switchMap(() => this.leaveSessionIfNeeded$()),
        ));
    }

    /** Удалить сессию на сервере, при необходимости предварительно выйдя из неё. */
    public deleteSession(sessionId: string): Observable<void> {
        return this.chainOperation(this.requestReadyConnection$('closing_session').pipe(
            switchMap(() => this.currentSessionProvider?.id === sessionId ? this.leaveSessionIfNeeded$() : of(undefined)),
            tap(() => {
                this.mode = 'idle';
                this.send({ type: 'delete_session', sessionId });
            }),
            map(() => undefined),
        ));
    }

    /** Отправить на сервер новое название сессии. */
    public setSessionTitle(sessionId: string, title: string): Observable<void> {
        return this.chainOperation(this.requestReadyConnection$(this.mode).pipe(
            tap(() => {
                this.send({ type: 'session_title', sessionId, title });
            }),
            map(() => undefined),
        ));
    }

    /** Полностью закрыть provider и сокет. */
    public destroy(): void {
        this.destroyed = true;
        this.clearReconnect();
        this.destroyCurrentSessionProvider();
        this.closeConnection();
        this.pendingOperation$ = EMPTY;
        this.eventsSubject.complete();
        this.sessionTakenOverSubject.complete();
    }


    /**
     * Сериализует операции: новый запрос стартует только после завершения предыдущего.
     * Ошибка предыдущего не блокирует очередь — следующий запрос всё равно выполнится.
     * resetOn* = false: иначе после complete share() при следующем concat заново
     * выполняет предыдущий source (например, повторный create_session при leave).
     */
    private chainOperation<T>(operation$: Observable<T>): Observable<T> {
        const result$ = concat(
            this.pendingOperation$.pipe(ignoreElements(), catchError(() => EMPTY)),
            operation$,
        ).pipe(share({
            resetOnError: false,
            resetOnComplete: false,
            resetOnRefCountZero: false,
        }));
        this.pendingOperation$ = result$;
        return result$;
    }

    private requestReadyConnection$(mode: ChatAgentProviderMode): Observable<void> {
        return defer((): Observable<void> => {
            this.mode = mode;

            if (!this.ws) {
                this.openConnection();
            }

            if (this.wsInitialized) {
                return of(undefined);
            }

            return this.eventsSubject.pipe(
                first(this.isReadyOrErrorEvent),
                map(event => {
                    if (event.kind === 'error') {
                        this.mode = 'idle';
                        throw event;
                    }
                }),
            );
        });
    }

    private openConnection(): void {
        this.discardConnection();
        this.ws = new AgentWsConnection(this.wsUrl);
        this.wsSubscription = this.ws.events$.subscribe({
            next: event => this.handleWsEvent(event),
            complete: () => this.onWsConnectionEnded(),
        });
    }

    private closeConnection(): void {
        this.clearReconnect();
        this.discardConnection();
        this.mode = 'idle';
    }

    private discardConnection(): void {
        this.wsSubscription?.unsubscribe();
        this.wsSubscription = null;
        this.wsInitialized = false;

        const ws = this.ws;
        this.ws = null;
        ws?.close();
    }

    private onWsConnectionEnded(): void {
        if (this.destroyed) {
            return;
        }

        this.handleConnectionLost();
    }

    private handleConnectionLost(): void {
        if (this.destroyed || this.isReconnecting) {
            return;
        }

        this.isReconnecting = true;
        this.discardConnection();
        this.currentSessionProvider?.setConnectionState('reconnecting');
        this.scheduleReconnect();
    }

    private scheduleReconnect(): void {
        if (this.destroyed || this.reconnectTimer) {
            return;
        }

        const delay = this.reconnectAttempt === 0
            ? INITIAL_RECONNECT_DELAY_MS
            : Math.min(INITIAL_RECONNECT_DELAY_MS * (2 ** this.reconnectAttempt), MAX_RECONNECT_DELAY_MS);

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.attemptReconnect();
        }, delay);
    }

    private attemptReconnect(): void {
        if (this.destroyed) {
            return;
        }

        const sessionId = this.currentSessionProvider?.id ?? null;
        this.reconnectAttempt += 1;
        this.currentSessionProvider?.setConnectionState('reconnecting');
        this.openConnection();

        this.reconnectSubscription?.unsubscribe();
        this.reconnectSubscription = this.eventsSubject.pipe(
            first(this.isReadyOrErrorEvent),
        ).subscribe(event => {
            this.reconnectSubscription = null;

            if (event.kind === 'ready') {
                this.reconnectAttempt = 0;
                this.rejoinSessionIfNeeded(sessionId);
                return;
            }

            if (this.isFatalAuthError(event)) {
                this.isReconnecting = false;
                this.currentSessionProvider?.setConnectionState('error');
                return;
            }

            this.scheduleReconnect();
        });
    }

    private rejoinSessionIfNeeded(sessionId: string | null): void {
        if (!sessionId || !this.currentSessionProvider) {
            this.isReconnecting = false;
            this.currentSessionProvider?.setConnectionState('connected');
            return;
        }

        this.mode = 'opening_session';
        this.send({ type: 'join_session', sessionId });

        this.reconnectSubscription?.unsubscribe();
        this.reconnectSubscription = this.eventsSubject.pipe(
            first(event => event.kind === 'session_ready' || event.kind === 'error'),
        ).subscribe(event => {
            this.reconnectSubscription = null;

            if (event.kind === 'session_ready') {
                this.mode = 'active_session';
                this.isReconnecting = false;
                this.currentSessionProvider?.setConnectionState('connected');
                this.currentSessionProvider?.loadHistory();
                return;
            }

            if (event.kind === 'error' && this.isFatalAuthError(event)) {
                this.isReconnecting = false;
                this.currentSessionProvider?.setConnectionState('error');
                return;
            }

            this.scheduleReconnect();
        });
    }

    private clearReconnect(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        this.reconnectSubscription?.unsubscribe();
        this.reconnectSubscription = null;
        this.reconnectAttempt = 0;
        this.isReconnecting = false;
    }

    private isFatalAuthError(event: WsErrorEvent): boolean {
        return event.code === '4001' || event.code === '4401' || event.error === 'unauthorized';
    }

    private isConnectionError(event: WsErrorEvent): boolean {
        return !event.messageId && (
            event.error === 'connection_closed'
            || event.error === 'websocket_error'
        );
    }

    private handleWsEvent(event: ServerEvent): void {
        switch (event.kind) {
            case 'connected':
                if (this.tools.length > 0) {
                    this.send({ type: 'declare_tools', tools: this.tools.map(t => t.declaration) });
                }
                if (this.blocks.length > 0) {
                    this.send({ type: 'declare_blocks', blocks: this.blocks });
                }
                this.send({
                    type: 'init_complete',
                    protocolVersion: AGENT_PROTOCOL_VERSION,
                    frontendVersion: AGENT_FRONTEND_VERSION,
                    tabId: this.tabId,
                });
                break;

            case 'ready':
                this.wsInitialized = true;
                this.eventsSubject.next(event);
                break;

            case 'sessions':
            case 'session_ready':
            case 'session_closed':
            case 'session_title':
                this.eventsSubject.next(event);
                break;

            case 'error':
                this.handleError(event);
                break;

            default:
                this.currentSessionProvider?.applyServerEvent(event);
        }
    }

    private handleError(event: WsErrorEvent): void {
        if (this.isConnectionError(event)) {
            this.handleConnectionLost();
            return;
        }

        if (event.code === AGENT_ERROR_CODE.sessionTakenOver && this.currentSessionProvider) {
            this.handleSessionTakenOver();
            return;
        }

        if (this.currentSessionProvider && (this.mode === 'active_session'
            || (this.wsInitialized && this.mode === 'loading_sessions' && event.code === 'rate_limit'))) {
            this.currentSessionProvider.applyServerEvent(event);
            return;
        }

        this.eventsSubject.next(event);
    }

    private handleSessionTakenOver(): void {
        this.destroyCurrentSessionProvider();
        this.sessionTakenOverSubject.next();
    }

    private activateSessionProvider(sessionId: string): ChatAgentSessionProvider {
        this.mode = 'active_session';
        this.currentSessionProvider = new ChatAgentSessionProvider(
            sessionId,
            command => this.send(command),
            data => this.ws?.sendBinary(data),
            this.tools,
        );
        return this.currentSessionProvider;
    }

    private send(command: ClientCommand): void {
        this.ws?.send(command);
    }

    private leaveSessionIfNeeded$(): Observable<void> {
        if (!this.currentSessionProvider) {
            this.mode = 'idle';
            return of(undefined);
        }

        const sessionId = this.currentSessionProvider.id;
        this.mode = 'closing_session';
        this.send({ type: 'leave_session' });

        return this.eventsSubject.pipe(
            first(event => {
                if (event.kind === 'error') {
                    return true;
                }

                return event.kind === 'session_closed' && event.sessionId === sessionId;
            }),
            tap(event => {
                this.destroyCurrentSessionProvider();

                if (event.kind === 'error') {
                    throw event;
                }
            }),
            map(() => undefined),
        );
    }

    private destroyCurrentSessionProvider(): void {
        this.currentSessionProvider?.destroy();
        this.currentSessionProvider = null;
        this.mode = 'idle';
    }

    private isReadyOrErrorEvent(event: ServerEvent): event is WsReadyEvent | WsErrorEvent {
        return event.kind === 'ready' || event.kind === 'error';
    }
}
